import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { decodeSearchParams } from '@pages/CaseDashboard/utils';
import { FiltersForm } from '@shared/types/forms';
import { getTodayDate } from '@utils/dates';

interface UseFiltersInitialDates {
  defaultStartDate: string;
  defaultEndDate: string;
  startDate: string;
  endDate: string;
  isDefaultDateRange: boolean;
}

export const useFiltersInitialDates = (): UseFiltersInitialDates => {
  const [searchParams] = useSearchParams();

  const filters: FiltersForm = decodeSearchParams(searchParams);

  const { selectedCase } = useUserOrgCase();
  const today = useMemo(() => getTodayDate(), []);

  const defaultStartDate = useMemo(() => {
    const configured = selectedCase?.claimSubmissionStartDate;
    const actual = selectedCase?.firstClaimSubmissionDate;
    if (!actual) return configured;
    return actual < configured ? actual : configured;
  }, [
    selectedCase?.firstClaimSubmissionDate,
    selectedCase?.claimSubmissionStartDate,
  ]);

  const defaultEndDate = useMemo(() => {
    const configured = selectedCase?.claimSubmissionEndDate;
    const configuredIsInFuture = !configured || configured >= today;

    if (!selectedCase || configuredIsInFuture) return today;

    const actual = selectedCase?.lastClaimSubmissionDate;
    const lastClaimIsLatestDate = actual !== undefined && actual > configured;
    return lastClaimIsLatestDate ? actual : configured;
  }, [selectedCase, today]);

  const startDate = useMemo(
    () => filters.startDate || defaultStartDate,
    [defaultStartDate, filters.startDate],
  );

  const endDate = useMemo(
    () => filters.endDate || defaultEndDate || today,
    [filters.endDate, defaultEndDate, today],
  );

  const isDefaultDateRange = useMemo(
    () =>
      !filters.startDate &&
      !filters.endDate &&
      filters.startDate === defaultStartDate &&
      filters.endDate === defaultEndDate,
    [filters.startDate, filters.endDate, defaultStartDate, defaultEndDate],
  );

  return {
    defaultStartDate,
    defaultEndDate,
    startDate,
    endDate,
    isDefaultDateRange,
  };
};
